import { ArrowRight, Lock, TrendingUp, X } from "lucide-react";

export type RestrictionVariant =
  | "dashboard"
  | "analyzeStock"
  | "patternInsights"
  | "orderBlock"
  | "orderBlockRetest"
  | "addStock"
  | "freeTrialStarted"
  | "default";

interface FreeTrialRestrictionModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: RestrictionVariant;
}

const PremiumText = () => (
  <span className="font-bold text-purple-600 dark:text-purple-400">
    Premium
  </span>
);

const RESTRICTION_CONTENT: Record<
  RestrictionVariant,
  { title: string; message: React.ReactNode; titleSize?: string }
> = {
  dashboard: {
    title: "15-Session Delay Active in Free trial plan",
    titleSize: "text-xl",
    message: (
      <>
        Switch to a <PremiumText /> plan to remove the delay.
      </>
    ),
  },

  analyzeStock: {
    title: "You’ve used your 3 free views for today. Don't stop your research",
    titleSize: "text-xl",
    message: (
      <>
        Upgrade to <PremiumText /> for unlimited daily access
      </>
    ),
  },

  patternInsights: {
    title: "You are currently viewing restricted timeframes",
    message: (
      <>
        Upgrade to <PremiumText /> for unrestricted access.
      </>
    ),
  },

  addStock: {
    title: `${Number(process.env.NEXT_PUBLIC_STOCK_COUNT_LIMIT)} stocks can be added in free trial`,
    message: (
      <>
        Upgrade to <PremiumText /> for unrestricted access.
      </>
    ),
  },

  orderBlock: {
    title: "Order block chain is a premium feature ",
    message: (
      <>
        Upgrade now to identify high-probability entries and perfect your timing
      </>
    ),
  },
  orderBlockRetest: {
    title: "Retest order block is a premium feature",
    message: (
      <>
        Upgrade now to identify high-probability entries and perfect your timing
      </>
    ),
  },

  default: {
    title: "Free Trial Limit Reached",
    message: (
      <>
        You&apos;ve reached your <PremiumText /> trial limit.
      </>
    ),
  },

  freeTrialStarted: {
    title: "Congratulations !!",
    message: (
      <>
        Your {process.env.NEXT_PUBLIC_FREE_TRIAL_DAYS || 14} days Free Trial
        Started
      </>
    ),
  },
};

const FreeTrialRestrictionModal = ({
  isOpen,
  onClose,
  variant = "default",
}: FreeTrialRestrictionModalProps) => {
  if (!isOpen) return null;

  const {
    title,
    message,
    titleSize = "text-2xl",
  } = RESTRICTION_CONTENT[variant];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#1E1E2D] rounded-xl shadow-2xl w-full max-w-lg animate-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 relative">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 dark:hover:text-white p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Icon */}
          <div className="relative flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full blur-xl opacity-50 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shadow-2xl">
                {variant === "freeTrialStarted" ? (
                  <TrendingUp className="w-10 h-10 text-white" />
                ) : (
                  <Lock className="w-10 h-10 text-white" />
                )}
              </div>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-6">
            <h3
              className={`${titleSize} font-bold text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent`}
            >
              {title}
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-purple-400 via-indigo-500 to-purple-400 mx-auto rounded-full mb-4" />
          </div>

          {/* Message */}
          <div className="mb-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-100 dark:from-purple-400/30 dark:via-indigo-400/30 dark:to-purple-400/20 rounded-xl" />
            <div className="absolute inset-0 bg-white/40 dark:bg-black/20 backdrop-blur-sm rounded-xl" />

            <div className="relative p-6 border-2 border-purple-200/50 dark:border-purple-700/50 rounded-xl text-center">
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto pt-2">
                {message}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
            {variant === "freeTrialStarted" ? (
              <button
                onClick={onClose}
                className="group flex-1 relative overflow-hidden px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl shadow-2xl transition-all hover:scale-[1.02] cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <div className="relative z-10 flex items-center justify-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Start Exploring
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            ) : (
              <>
                <button
                  onClick={() => (window.location.href = "/billing")}
                  className="group flex-1 relative overflow-hidden px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl shadow-2xl transition-all hover:scale-[1.02] cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  <div className="relative z-10 flex items-center justify-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Upgrade Now
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>

                <button
                  onClick={onClose}
                  className="px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  Maybe Later
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeTrialRestrictionModal;
